import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../../lib/ui.js';
import { Card, Tabs } from '../../lib/components.js';
import { NotificationsControl } from '../NotificationsControl.js';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { fmtTime } from '@siva/shared';
import { useConfirm } from '../../lib/Modal.js';
import { SlideOver } from '../../lib/SlideOver.js';
import { AdminOverview } from '../AdminOverview.js';
import { AndroidCard } from '../AndroidCard.js';
import { t } from '../../i18n/index.js';

type Status = 'green' | 'amber' | 'red' | 'grey';
type Health = {
  checked_at: string;
  api: { status: Status; detail: string; uptime_s: number; node: string };
  database: { status: Status; detail: string; latency_ms: number | null };
  backups: { status: Status; detail: string; last: Record<string, unknown> | null };
  push: { status: Status; detail: string };
  email: { status: Status; detail: string };
  server: {
    disk: {
      total_bytes: number;
      free_bytes: number;
      used_percent: number | null;
      status: Status;
    } | null;
    memory: {
      rss_bytes: number;
      heap_used_bytes: number;
      limit_bytes: number | null;
      host_total_bytes: number;
      host_free_bytes: number;
    };
  };
};
type Jobs = {
  server_time: string;
  jobs: {
    name: string;
    description: string;
    next_run: string | null;
    last_run: {
      status: 'running' | 'success' | 'failed';
      scheduled_for: string;
      finished_at: string | null;
      error: string | null;
    } | null;
  }[];
  runs: {
    id: string;
    job: string;
    scheduled_for: string;
    status: 'running' | 'success' | 'failed';
    finished_at: string | null;
    error: string | null;
    details: Record<string, unknown> | null;
  }[];
};
type Run = Jobs['runs'][number];

type Logs = {
  errors: {
    id: string;
    at: string;
    request_id: string;
    method: string;
    route: string;
    status: number;
    code: string;
    message: string;
  }[];
  notification_failures: {
    id: string;
    at: string;
    channel: string;
    kind: string;
    to: string | null;
    destination: string | null;
    status: string;
    attempts: number;
    error: string | null;
    retriable: boolean;
  }[];
  backups: {
    id: string;
    started_at: string;
    finished_at: string;
    label: string;
    file: string;
    size_bytes: number;
    s3_status: string;
    monthly_copy: boolean;
    error: string | null;
  }[];
};

const JOB_LABEL: Record<string, { label: string; desc: string }> = {
  auto_close: { label: t('job_auto_close'), desc: t('job_auto_close_desc') },
  admin_digest: { label: t('job_admin_digest'), desc: t('job_admin_digest_desc') },
  daily_report: { label: t('job_daily_report'), desc: t('job_daily_report_desc') },
  data_retention: { label: t('job_data_retention'), desc: t('job_data_retention_desc') },
  monthly_report: { label: t('job_monthly_report'), desc: t('job_monthly_report_desc') },
  weekly_off_draft: { label: t('job_weekly_off_draft'), desc: t('job_weekly_off_draft_desc') },
  weekly_off_publish: {
    label: t('job_weekly_off_publish'),
    desc: t('job_weekly_off_publish_desc'),
  },
};
const jobLabel = (name: string) => JOB_LABEL[name]?.label ?? name;

const gb = (b: number) => `${(b / 1024 ** 3).toFixed(1)} GB`;
const mb = (b: number) => `${Math.round(b / 1024 ** 2)} MB`;
const statusWord = (s: Status) =>
  s === 'green'
    ? t('health_ok')
    : s === 'amber'
      ? t('health_attention')
      : s === 'red'
        ? t('health_down')
        : t('health_not_configured');

const summarise = (d: Record<string, unknown> | null): string => {
  if (!d) return '';
  const parts: string[] = [];
  if (d.manual) parts.push('manual');
  if (d.dry_run) parts.push('preview');
  if (typeof d.closed === 'number') parts.push(`closed ${d.closed}`);
  if (Array.isArray(d.still_in)) parts.push(`still in: ${d.still_in.length}`);
  if (typeof d.scheduled === 'number') parts.push(`scheduled ${d.scheduled}`);
  if (typeof d.deleted_notifications === 'number')
    parts.push(
      `deleted ${d.deleted_notifications} notifications, ${d.deleted_punch_attempts} attempts`,
    );
  if (d.skipped) parts.push('skipped: nobody scheduled');
  if (typeof d.notifications_queued === 'number') parts.push(`queued ${d.notifications_queued}`);
  if (d.stub) parts.push('stub');
  return parts.join(' · ');
};

function HealthCell({
  label,
  status,
  detail,
  testId,
}: {
  label: string;
  status: Status;
  detail: string;
  testId: string;
}) {
  return (
    <div className="health-cell" data-testid={testId} data-status={status}>
      <span className={`h-dot ${status}`} aria-hidden="true" />
      <span className="h-body">
        <span className="h-label">{label}</span>
        <span className="h-value">{statusWord(status)}</span>
        <span className="h-detail" title={detail}>
          {detail}
        </span>
      </span>
    </div>
  );
}

export function SystemPage() {
  const confirm = useConfirm();
  const [health, setHealth] = useState<Health | null>(null);
  const [data, setData] = useState<Jobs | null>(null);
  const [logs, setLogs] = useState<Logs | null>(null);
  const [tab, setTab] = useState<'errors' | 'notifications' | 'backups'>('errors');
  /** An extra backup, on top of the nightly one. Reloads so the logs show it straight away. */
  const [backupBusy, setBackupBusy] = useState(false);
  const runBackup = async () => {
    setBackupBusy(true);
    try {
      await post('/admin/backups/run', {});
      await load();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBackupBusy(false);
    }
  };
  const [techOpen, setTechOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * "See Why" on the attention list points at this page with a hash. A link to the page one is
   * already on changes only the hash, and the target was a collapsed expander further down, so
   * the button looked like it did nothing. The hash opens the section, picks the tab it names
   * and scrolls there; location.key makes a second click on the same link do it again.
   */
  const location = useLocation();
  useEffect(() => {
    const target = location.hash.replace('#', '');
    if (!['technical', 'errors', 'notifications', 'backups'].includes(target)) return;
    setTechOpen(true);
    if (target !== 'technical') setTab(target as 'errors' | 'notifications' | 'backups');
    const id = window.setTimeout(() => {
      document
        .getElementById(target === 'technical' ? 'technical' : 'technical-logs')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(id);
  }, [location.hash, location.key]);
  const [run, setRun] = useState<Run | null>(null);

  const retry = async (id: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await post<{ sent: number; failed: number }>(`/admin/notifications/${id}/retry`);
      setMsg({
        kind: r.failed ? 'error' : 'success',
        text: t('logs_retry_result', { sent: r.sent, failed: r.failed }),
      });
      await load();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  const [msg, setMsg] = useState<{ kind: 'error' | 'success' | 'info'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, j, l] = await Promise.all([
        api<Health>('/admin/health'),
        api<Jobs>('/admin/jobs'),
        api<Logs>('/admin/logs'),
      ]);
      setHealth(h);
      setData(j);
      setLogs(l);
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }, []);
  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  type Outcome = {
    status: string;
    result?: Record<string, unknown>;
    error?: string;
    reason?: string;
  };

  const describe = (name: string, r: Record<string, unknown> | undefined): string => {
    if (!r) return '';
    if (name === 'auto_close') {
      const list = (r.would_close as { name: string }[] | undefined) ?? [];
      return list.length
        ? t('jobs_preview_auto_close', {
            n: list.length,
            names: list.map((x) => x.name).join(', '),
          })
        : t('jobs_preview_auto_close_none');
    }
    if (name === 'monthly_report') {
      if (r.skipped === 'disabled') return t('jobs_preview_monthly_off');
      return t('jobs_preview_monthly', {
        month: String(r.month ?? ''),
        summary: String(r.summary ?? ''),
      });
    }
    if (name === 'data_retention') {
      return t('jobs_preview_retention', {
        n: Number(r.would_delete_notifications ?? 0),
        p: Number(r.would_delete_punch_attempts ?? 0),
      });
    }
    if (name === 'daily_report') {
      const day = String(r.day ?? '');
      return r.skipped
        ? t('jobs_preview_daily_report_none', { day })
        : t('jobs_preview_daily_report', { day, summary: String(r.summary ?? '') });
    }
    if (name === 'admin_digest') {
      const list = (r.still_in as string[] | undefined) ?? [];
      return list.length
        ? t('jobs_preview_digest', { n: list.length, names: list.join(', ') })
        : t('jobs_preview_digest_none');
    }
    return JSON.stringify(r);
  };

  const preview = async (name: string): Promise<Outcome | null> => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await post<{ outcome: Outcome }>(`/admin/jobs/${name}/run`, { dry_run: true });
      return r.outcome;
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  /** Preview only: show what the job would do. */
  const dryRun = async (name: string) => {
    const o = await preview(name);
    if (o) setMsg({ kind: 'info', text: `${jobLabel(name)}: ${describe(name, o.result)}` });
  };

  /** Run now: always show the dry-run result first and require typing RUN. */
  const askToRun = async (name: string) => {
    const o = await preview(name);
    if (!o) return;
    const ok = await confirm({
      title: t('jobs_confirm_title', { job: jobLabel(name) }),
      body: (
        <>
          <p>{describe(name, o.result)}</p>
          <p className="muted">{t('jobs_confirm_type_run')}</p>
        </>
      ),
      confirmLabel: t('jobs_run_now'),
      danger: true,
      typeToConfirm: 'RUN',
    });
    if (!ok) return;
    await confirmRun(name);
  };

  const confirmRun = async (name: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await post<{ outcome: Outcome }>(`/admin/jobs/${name}/run`, { dry_run: false });
      const o = r.outcome;
      setMsg({
        kind: o.status === 'failed' ? 'error' : 'info',
        text: `${jobLabel(name)}: ${o.status}${o.reason ? ` (${o.reason})` : ''} ${o.result ? JSON.stringify(o.result) : ''}${o.error ?? ''}`,
      });
      await load();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await post<{ days_written: number; workers: number }>('/admin/recompute', {});
      setMsg({
        kind: 'success',
        text: t('jobs_recompute_done', { days: r.days_written, workers: r.workers }),
      });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const runBadge = (s: 'running' | 'success' | 'failed') => (
    <span className={`badge ${s === 'success' ? 'good' : s === 'failed' ? 'bad' : ''}`}>{s}</span>
  );

  return (
    <div className="stack">
      <AdminOverview onRefresh={() => void load()} />

      {/* The Flags card left this page on 16 October 2026: Flags is in the sidebar with its open
          count, and Needs Your Attention above already links there. */}

      {/*
        The Android App card left this page on 17 September 2026, when the app moved to the Play
        Store and nobody downloaded the build from here any more. It came back on 29 September,
        because two of the things it carries were never about hosting a file: which build each
        phone is running, and the minimum the server enforces at the punch. Without them, "sign-in
        does nothing on Ravi's phone" is a mystery rather than "Ravi's app is version 26" — which
        is what it turned out to be. Uploading a build is still behind its own button, still the
        fallback PLAY-STORE.md describes, and still not what this card is for day to day.
      */}
      <AndroidCard />

      {/* Everything below is the old System page: true, useful when something is wrong, and not
          what the owner opens this page to find out. */}
      <details
        className="technical"
        id="technical"
        open={techOpen}
        onToggle={(e) => setTechOpen(e.currentTarget.open)}
      >
        <summary>{t('admin_technical')}</summary>
        <div className="stack">
      {/*
        Taking an extra backup is maintenance, not something the owner needs offered every time
        they open the page — it sat above the fold next to what needs attention today. It lives
        with the health strip and the logs now, which is where somebody goes when they are
        thinking about the machine rather than about the shift.

        'grey' is how the health endpoint reports backups that are not configured at all; any
        other status means there is somewhere for a backup to go.
      */}
      <Card title={t('admin_maintenance')}>
        <div className="row">
          <button
            type="button"
            className="secondary"
            disabled={backupBusy || health?.backups.status === 'grey'}
            title={health?.backups.status === 'grey' ? t('admin_backup_not_set_up') : undefined}
            onClick={() => void runBackup()}
          >
            <RefreshCw size={15} aria-hidden="true" /> {t('admin_backup_now')}
          </button>
        </div>
        <p className="muted">{t('admin_backup_help')}</p>
      </Card>
      <Card
        title={t('health_title')}
        subtitle={
          health
            ? t('health_checked', {
                time: fmtTime(health.checked_at),
              })
            : undefined
        }
      >
        {!health ? (
          <Spinner />
        ) : (
          <div className="health-strip" role="list" aria-label={t('health_title')}>
            <HealthCell
              testId="health-api"
              label={t('health_api')}
              status={health.api.status}
              detail={health.api.detail}
            />
            <HealthCell
              testId="health-database"
              label={t('health_database')}
              status={health.database.status}
              detail={health.database.detail}
            />
            <HealthCell
              testId="health-backups"
              label={t('health_backups')}
              status={health.backups.status}
              detail={health.backups.detail}
            />
            <HealthCell
              testId="health-push"
              label={t('health_push')}
              status={health.push.status}
              detail={health.push.detail}
            />
            <HealthCell
              testId="health-email"
              label={t('health_email')}
              status={health.email.status}
              detail={health.email.detail}
            />
          </div>
        )}
      </Card>

      <NotificationsControl />

      <Card title={t('jobs_title')} subtitle={t('jobs_intro')}>
        {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
        {!data ? (
          <Spinner />
        ) : (
          <>
            <div className="table-wrap">
              <table className="jobs-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th className="when">{t('jobs_last')}</th>
                    <th className="when">{t('jobs_next')}</th>
                    <th>{t('worker_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.name}>
                      <td>
                        <strong>{jobLabel(j.name)}</strong>
                        <br />
                        <span className="muted">{JOB_LABEL[j.name]?.desc ?? j.description}</span>
                      </td>
                      <td className="when">
                        {j.last_run ? (
                          <>
                            {runBadge(j.last_run.status)}{' '}
                            <span className="muted">
                              {fmtDate(j.last_run.finished_at ?? j.last_run.scheduled_for)}
                            </span>
                            {j.last_run.error && (
                              <div className="muted log-message">{j.last_run.error}</div>
                            )}
                          </>
                        ) : (
                          <span className="muted">{t('jobs_never')}</span>
                        )}
                      </td>
                      <td className="when">{fmtDate(j.next_run)}</td>
                      <td className="actions">
                        <span className="row">
                          <button
                            type="button"
                            className="small"
                            onClick={() => dryRun(j.name)}
                            disabled={busy}
                          >
                            {t('jobs_dry_run')}
                          </button>
                          <button
                            type="button"
                            className="small secondary"
                            onClick={() => askToRun(j.name)}
                            disabled={busy}
                            title={t('jobs_run_now_hint')}
                          >
                            <TriangleAlert size={14} aria-hidden="true" />
                            {t('jobs_run_now')}
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <details className="runs">
              <summary>{t('jobs_history')}</summary>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Slot</th>
                      <th>{t('worker_status')}</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.runs.map((r) => (
                      <tr
                        key={r.id}
                        className="clickable"
                        onClick={() => setRun(r)}
                        title={t('jobs_run_details')}
                      >
                        <td>{jobLabel(r.job)}</td>
                        <td>{fmtDate(r.scheduled_for)}</td>
                        <td>{runBadge(r.status)}</td>
                        <td className="muted">{r.error ?? summarise(r.details)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <h4>{t('jobs_maintenance')}</h4>
            <p>
              <button type="button" className="small secondary" onClick={recompute} disabled={busy}>
                {t('jobs_recompute')}
              </button>
            </p>
          </>
        )}
        <SlideOver
          open={!!run}
          title={run ? jobLabel(run.job) : ''}
          subtitle={run ? fmtDate(run.scheduled_for) : ''}
          onClose={() => setRun(null)}
        >
          {run && (
            <div className="stack">
              <p className="row">
                {runBadge(run.status)}
                <span className="muted">{fmtDate(run.finished_at ?? run.scheduled_for)}</span>
              </p>
              {run.error && <Alert>{run.error}</Alert>}
              <p>{describe(run.job, run.details ?? undefined) || summarise(run.details)}</p>
              <pre className="run-json">{JSON.stringify(run.details ?? {}, null, 2)}</pre>
            </div>
          )}
        </SlideOver>
      </Card>

      <Card id="technical-logs" title={t('logs_title')}>
        {!logs || !health ? (
          <Spinner />
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={setTab}
              items={[
                { value: 'errors', label: `${t('logs_errors')} (${logs.errors.length})` },
                {
                  value: 'notifications',
                  label: `${t('logs_notifications')} (${logs.notification_failures.length})`,
                },
                { value: 'backups', label: `${t('logs_backups')} (${logs.backups.length})` },
              ]}
            />
            {tab === 'errors' &&
              (logs.errors.length === 0 ? (
                <p className="muted">{t('logs_none_errors')}</p>
              ) : (
                <div className="table-wrap logs-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('logs_col_time')}</th>
                        <th>{t('logs_col_request')}</th>
                        <th>{t('logs_col_route')}</th>
                        <th>{t('logs_col_message')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.errors.map((e) => (
                        <tr key={e.id}>
                          <td>{fmtDate(e.at)}</td>
                          <td className="mono">{e.request_id}</td>
                          <td className="mono">
                            {e.method} {e.route}
                            <br />
                            <span className="muted">
                              {e.status} {e.code}
                            </span>
                          </td>
                          <td className="log-message">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            {tab === 'notifications' &&
              (logs.notification_failures.length === 0 ? (
                <p className="muted">{t('logs_none_notifications')}</p>
              ) : (
                <div className="table-wrap logs-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('logs_col_time')}</th>
                        <th>{t('logs_col_channel')}</th>
                        <th>{t('logs_col_to')}</th>
                        <th>{t('logs_col_error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.notification_failures.map((n) => (
                        <tr key={n.id}>
                          <td>{fmtDate(n.at)}</td>
                          <td>
                            {n.channel} · {n.kind}
                            <br />
                            <span className="muted">
                              {n.status}, {n.attempts} attempt(s)
                            </span>
                          </td>
                          <td>
                            {n.to ?? '—'}
                            {n.destination && (
                              <>
                                <br />
                                <span className="muted">{n.destination}</span>
                              </>
                            )}
                          </td>
                          <td className="log-message">
                            {n.error ?? '—'}
                            {n.retriable && (
                              <button
                                type="button"
                                className="small secondary retry-inline"
                                disabled={busy}
                                onClick={() => void retry(n.id)}
                              >
                                {t('logs_retry')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            {tab === 'backups' &&
              (logs.backups.length === 0 ? (
                <p className="muted">{t('logs_none_backups')}</p>
              ) : (
                <div className="table-wrap logs-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('logs_col_time')}</th>
                        <th>{t('logs_col_file')}</th>
                        <th>{t('logs_col_size')}</th>
                        <th>{t('logs_col_s3')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.backups.map((b) => (
                        <tr key={b.id}>
                          <td>
                            {fmtDate(b.finished_at)}
                            <br />
                            <span className="muted">{b.label}</span>
                          </td>
                          <td className="mono">{b.file}</td>
                          <td>{mb(b.size_bytes)}</td>
                          <td>
                            <span
                              className={`badge ${b.s3_status === 'uploaded' ? 'good' : b.s3_status === 'failed' ? 'bad' : 'warn'}`}
                            >
                              {b.s3_status === 'uploaded'
                                ? t('logs_s3_uploaded')
                                : b.s3_status === 'failed'
                                  ? t('logs_s3_failed')
                                  : t('logs_s3_skipped')}
                            </span>
                            {b.monthly_copy && (
                              <span className="badge">{t('logs_monthly_copy')}</span>
                            )}
                            {b.error && <div className="muted log-message">{b.error}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

            <h4>{t('logs_server')}</h4>
            <dl>
              <dt>{t('logs_disk')}</dt>
              <dd>
                {health.server.disk
                  ? `${t('logs_of', {
                      used: gb(health.server.disk.total_bytes - health.server.disk.free_bytes),
                      total: gb(health.server.disk.total_bytes),
                    })} (${health.server.disk.used_percent}%)`
                  : '—'}
              </dd>
              <dt>{t('logs_memory')}</dt>
              <dd>
                {t('logs_of', {
                  used: mb(health.server.memory.rss_bytes),
                  total: health.server.memory.limit_bytes
                    ? mb(health.server.memory.limit_bytes)
                    : t('logs_no_limit'),
                })}
              </dd>
            </dl>
          </>
        )}
      </Card>
        </div>
      </details>
    </div>
  );
}
